import React from 'react'
import { Groupbox, ModalFooter, ModalWindow, CollapsePanel } from 'view/components/panels'
import styled from '@emotion/styled'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import * as constants from 'view/styles/constants'
import { ClearingHouseInteraction, DateFormat } from 'api'
import { Locale, Translation } from 'locale'
import { ClearingMessageTypeHelper } from 'model'
import { formatTimestamp } from 'locale/Locale'
import { CollapseContentWrapper } from 'view/shared/ticket'
import { getDealEventText } from 'view/routes/deals/components/utils'

export interface ClearingHouseInteractionDialogProps {
  clearingHouseInteraction: ClearingHouseInteraction
  onClose?: () => unknown
}

const DialogContent = styled(Groupbox)``

const DialogContentInnerWrapper = styled.div`
  height: 380px;
  width: 430px;
  margin-bottom: 10px;
  overflow: auto;
  & > * {
    margin-bottom: 1px;
  }
`

const DialogSection = styled.div`
  margin-bottom: 10px;
`

const DialogTitle = styled.div`
  font-size: 16px;
  font-weight: ${constants.FontWeight.SemiBold};
  color: ${constants.CORE_PRIMARY_7};
`

const ErrorContent = styled.div`
  color: ${constants.ACCENT_BAD_1};
`

const ResolvedContent = styled.div`
  color: ${constants.ACCENT_BE_AWARE_1};
`

export const ClearingHouseInteractionDialog: React.FunctionComponent<ClearingHouseInteractionDialogProps> = props => {
  const { clearingHouseInteraction, onClose } = props
  const {
    id,
    messageType,
    messageSentInstant,
    message,
    pendingResponseReceivedInstant,
    pendingResponse,
    responseReceivedInstant,
    response,
    error,
    errorInstant,
    errorResolutionEvent,
    errorResolutionInstant
  } = clearingHouseInteraction

  const messageLines = message ? message.split('\n') : []
  const responseLines = response ? response.split('\n') : []
  const pendingResponseLines = pendingResponse ? pendingResponse.split('\n') : []

  const showRequestSection = !!(messageSentInstant || message)
  const showPendingResponseSection = !!(pendingResponseReceivedInstant || pendingResponse)
  const showResponseSection = !!(responseReceivedInstant || response)

  return (
    <ModalWindow title={Locale.translate(Translation.TC_CLEARING_INFO)}>
      <DialogContent>
        <DialogContentInnerWrapper>
          <CollapsePanel title={Locale.translate(Translation.TC_CLEARING_MESSAGE_SUMMARY)} defaultCollapsed={false}>
            <CollapseContentWrapper>
              <DialogSection>
                <DialogTitle>{Locale.translate(Translation.TC_CLEARING_MESSAGE_ID)}</DialogTitle>
                <div>{id}</div>
              </DialogSection>
              <DialogSection>
                <DialogTitle>{Locale.translate(Translation.TC_CLEARING_MESSAGE_TYPE)}</DialogTitle>
                <div>{ClearingMessageTypeHelper.getName(messageType)}</div>
              </DialogSection>
              {errorInstant && (
                <DialogSection>
                  <DialogTitle>{Locale.translate(Translation.TC_CLEARING_ERROR_AT)}</DialogTitle>
                  <ErrorContent>{formatTimestamp(errorInstant, DateFormat.EU)}</ErrorContent>
                </DialogSection>
              )}
              {error && (
                <DialogSection>
                  <DialogTitle>{Locale.translate(Translation.TC_CLEARING_ERROR_CONTENT)}</DialogTitle>
                  <ErrorContent>{error}</ErrorContent>
                </DialogSection>
              )}
              {errorResolutionInstant && (
                <DialogSection>
                  <DialogTitle>{Locale.translate(Translation.TC_CLEARING_ERROR_RESOLUTION_AT)}</DialogTitle>
                  <ResolvedContent>{formatTimestamp(errorResolutionInstant, DateFormat.EU)}</ResolvedContent>
                </DialogSection>
              )}
              {errorResolutionEvent && (
                <DialogSection>
                  <DialogTitle>{Locale.translate(Translation.TC_CLEARING_ERROR_RESOLUTION_CONTENT)}</DialogTitle>
                  <ResolvedContent>{getDealEventText(errorResolutionEvent)}</ResolvedContent>
                </DialogSection>
              )}
            </CollapseContentWrapper>
          </CollapsePanel>
          {showRequestSection && (
            <CollapsePanel title={Locale.translate(Translation.TC_CLEARING_REQUEST_DETAILS)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                {messageSentInstant && (
                  <DialogSection>
                    <DialogTitle>{Locale.translate(Translation.TC_CLEARING_REQUEST_SENT_AT)}</DialogTitle>
                    {formatTimestamp(messageSentInstant, DateFormat.EU)}
                  </DialogSection>
                )}
                {message && (
                  <DialogSection>
                    <DialogTitle>{Locale.translate(Translation.TC_CLEARING_REQUEST_CONTENT)}</DialogTitle>
                    {messageLines.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </DialogSection>
                )}
              </CollapseContentWrapper>
            </CollapsePanel>
          )}
          {showPendingResponseSection && (
            <CollapsePanel title={Locale.translate(Translation.TC_CLEARING_PENDING_RESPONSE_DETAILS)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                {pendingResponseReceivedInstant && (
                  <DialogSection>
                    <DialogTitle>{Locale.translate(Translation.TC_CLEARING_PENDING_RESPONSE_RECEIVED_AT)}</DialogTitle>
                    {formatTimestamp(pendingResponseReceivedInstant, DateFormat.EU)}
                  </DialogSection>
                )}
                {pendingResponse && (
                  <DialogSection>
                    <DialogTitle>{Locale.translate(Translation.TC_CLEARING_PENDING_RESPONSE_CONTENT)}</DialogTitle>
                    {pendingResponseLines.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </DialogSection>
                )}
              </CollapseContentWrapper>
            </CollapsePanel>
          )}
          {showResponseSection && (
            <CollapsePanel title={Locale.translate(Translation.TC_CLEARING_RESPONSE_DETAILS)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                {responseReceivedInstant && (
                  <DialogSection>
                    <DialogTitle>{Locale.translate(Translation.TC_CLEARING_RESPONSE_RECEIVED_AT)}</DialogTitle>
                    {formatTimestamp(responseReceivedInstant, DateFormat.EU)}
                  </DialogSection>
                )}
                {response && (
                  <DialogSection>
                    <DialogTitle>{Locale.translate(Translation.TC_CLEARING_RESPONSE_CONTENT)}</DialogTitle>
                    {responseLines.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </DialogSection>
                )}
              </CollapseContentWrapper>
            </CollapsePanel>
          )}
        </DialogContentInnerWrapper>
      </DialogContent>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={onClose}>
              {Locale.translate(Translation.TC_BUTTON_CLOSE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
